import * as yup from "yup";
import { LegalAge, UserSex } from "../utils/enums";
import { nipValidator, regon9Validator, peselValidator } from "./common";
import { startOfDay, subYears } from "date-fns";

export const CreateUserValidationSchema = yup.object().shape({
  login: yup
    .string()
    .required("Login jest polem wymaganym")
    .max(255, "Login może zawierać maksymalnie 255 znaków")
    .email("Wartość w polu Login nie jest prawidłowym formatem adresu e-mail"),
  email: yup
    .string()
    .required("E-mail jest polem wymaganym")
    .max(100, "E-mail może zawierać maksymalnie 100 znaków")
    .email("Wartość w polu E-mail nie jest prawidłowym formatem adresu e-mail"),
  firstname: yup
    .string()
    .max(30, "Imię może zawierać maksymalnie 30 znaków")
    .nullable(),
  surname: yup
    .string()
    .max(40, "Nazwisko może zawierać maksymalnie 40 znaków")
    .nullable(),
  phone: yup
    .string()
    .max(30, "Telefon może zawierać maksymalnie 30 znaków")
    .nullable(),
  mobilePhone: yup
    .string()
    .max(30, "Telefon komórkowy może zawierać maksymalnie 40 znaków")
    .nullable(),
  fax: yup
    .string()
    .max(50, "Fax może zawierać maksymalnie 50 znaków")
    .nullable(),
  idNumber: yup
    .string()
    .max(50, "Nr i seria dowodu osobistego może zawierać maksymalnie 50 znaków")
    .nullable(),
  pesel: yup
    .string()
    .required("PESEL jest polem wymaganym")
    .max(11, "PESEL może zawierać maksymalnie 11 znaków")
    .test("pesel-validator", "Nieprawidłowy numer PESEL", (value) => {
      if (value && value !== "") {
        return peselValidator(value);
      }
      return true;
    }),
  nip: yup
    .string()
    .max(15, "NIP nie może przekraczać 15 znaków")
    .test("nip-validator", "Nieprawidłowy numer NIP", (value) => {
      if (value && value !== "") {
        return nipValidator(value);
      }
      return true;
    })
    .nullable(),
  regon: yup
    .string()
    .max(14, "REGON nie może przekraczać 14 znaków")
    .test("regon-validator", "Nieprawidłowy numer REGON", (value) => {
      if (value && value !== "") {
        return regon9Validator(value);
      }
      return true;
    })
    .nullable(),
  permanentAddressStreet: yup
    .string()
    .max(100, "Ulica może zawierać maksymalnie 100 znaków")
    .nullable(),
  permanentAddressNumber: yup
    .string()
    .max(15, "Nr budynku może zawierać maksymalnie 15 znaków")
    .nullable(),
  permanentAddressFlat: yup
    .string()
    .max(15, "Nr lokalu może zawierać maksymalnie 15 znaków")
    .nullable(),
  permanentAddressPostCode: yup
    .string()
    .max(6, "Kod pocztowy może zawierać maksymalnie 6 znaków")
    .nullable(),
  permanentAddressPost: yup
    .string()
    .max(50, "Poczta może zawierać maksymalnie 50 znaków")
    .nullable(),
  permanentAddressCity: yup
    .string()
    .max(50, "Miasto może zawierać maksymalnie 50 znaków")
    .nullable(),
  permanentAddressCommunity: yup
    .string()
    .max(40, "Gmina może zawierać maksymalnie 40 znaków")
    .nullable(),
  permanentAddressDistrict: yup
    .string()
    .max(40, "Powiat może zawierać maksymalnie 40 znaków")
    .nullable(),
  permanentAddressProvince: yup
    .string()
    .max(50, "Województwo może zawierać maksymalnie 50 znaków")
    .nullable(),
  temporaryAddressStreet: yup
    .string()
    .max(100, "Ulica może zawierać maksymalnie 100 znaków")
    .nullable(),
  temporaryAddressNumber: yup
    .string()
    .max(15, "Nr budynku może zawierać maksymalnie 15 znaków")
    .nullable(),
  temporaryAddressFlat: yup
    .string()
    .max(15, "Nr lokalu może zawierać maksymalnie 15 znaków")
    .nullable(),
  temporaryAddressPostCode: yup
    .string()
    .max(6, "Kod pocztowy może zawierać maksymalnie 6 znaków")
    .nullable(),
  temporaryAddressPost: yup
    .string()
    .max(50, "Poczta może zawierać maksymalnie 50 znaków")
    .nullable(),
  temporaryAddressCity: yup
    .string()
    .max(50, "Miasto może zawierać maksymalnie 50 znaków")
    .nullable(),
  temporaryAddressCommunity: yup
    .string()
    .max(40, "Gmina może zawierać maksymalnie 40 znaków")
    .nullable(),
  temporaryAddressDistrict: yup
    .string()
    .max(40, "Powiat może zawierać maksymalnie 40 znaków")
    .nullable(),
  temporaryAddressProvince: yup
    .string()
    .max(50, "Województwo może zawierać maksymalnie 50 znaków")
    .nullable(),
  isCitizen: yup.boolean(),
  officeId: yup.number().when("isCitizen", {
    is: true,
    then: yup
      .number()
      .min(1, "Po zaznaczeniu pola Jestem mieszkańcem należy wybrać jednostkę"),
    otherwise: yup
      .number()
      .min(
        0,
        "Jednostka nie może być wybrany gdy pole Jestem mieszkańcem nie jest zaznaczone"
      )
      .max(
        0,
        "Jednostka nie może być wybrany gdy pole Jestem mieszkańcem nie jest zaznaczone"
      )
      .nullable(),
  }),
});

export const LoginUserValidationSchema = yup.object().shape({
  login: yup
    .string()
    .required("Login jest polem wymaganym")
    .max(255, "Login może zawierać maksymalnie 255 znaków")
    .email("Wartość w polu Login nie jest prawidłowym formatem adresu e-mail"),
  password: yup.string().required("Hasło jest polem wymaganym"),
});

export const UserVoteSchema = ({
  isDistrictHidden,
  isZipCodeRequiredWhileVotingNotLoggedUser,
}) => {
  return yup.object().shape({
    userSex: yup
      .number()
      .oneOf(
        [UserSex.Male.value, UserSex.Female.value],
        "Forma grzecznościowa musi przyjąć jedną z wymienionych wartości"
      ),
    firstname: yup
      .string()
      .matches(
        new RegExp(/^[a-zA-Z\s\p{L}]+$/, "u"),
        "Imię zawiera nieprawidłowe znaki. Dozwolone są tylko litery i znaki specjalne (spacja)"
      )
      .max(30, "Imię może zawierać maksymalnie 30 znaków")
      .required("Imię jest wymagane"),
    surname: yup
      .string()
      .matches(
        new RegExp(/^[a-zA-Z\s\p{L}\-]+$/, "u"),
        "Nazwisko zawiera nieprawidłowe znaki. Dozwolone są tylko litery i znaki specjalne (spacja, -)"
      )
      .max(40, "Nazwisko może zawierać maksymalnie 40 znaków")
      .required("Nazwisko jest wymagane"),
    pesel: yup
      .string()
      .test("pesel-validator", "Nieprawidłowy numer PESEL", (value) => {
        if (value && value !== "") {
          return peselValidator(value);
        }
        return true;
      })
      .max(11, "PESEL może zawierać maksymalnie 11 znaków")
      .when("usePeselForNotLogged", {
        is: true,
        then: yup.string().required("PESEL jest polem wymaganym"),
      }),
    birthDate: yup.date().when(["onlyForLegalAge"], (onlyForLegalAge) =>
      onlyForLegalAge
        ? yup
            .date()
            .required("Data urodzenia jest wymagana")
            .max(
              subYears(startOfDay(new Date()), LegalAge),
              "Głosowanie przeznaczone jest dla osób pełnoletnich"
            )
        : yup
            .date()
            .required("Data urodzenia jest wymagana")
            .max(
              new Date(),
              "Data urodzenia nie może być późniejsza niż aktualna data"
            )
    ),
    districtId: isDistrictHidden
      ? yup
          .object()
          .shape({
            label: yup.string(),
            value: yup.number(),
          })
          .nullable()
      : yup
          .object()
          .shape({
            label: yup.string(),
            value: yup.number(),
          })
          .nullable()
          .required("Obszar jest wymagany"),
    city: yup
      .string()
      .matches(
        new RegExp(/^[a-zA-Z\s\p{L}\/\-]+$/, "u"),
        "Miejscowość zawiera nieprawidłowe znaki. Dozwolone są tylko litery i znaki specjalne (spacja, -, /)"
      )
      .max(50, "Miejscowość może zawierać maksymalnie 50 znaków")
      .when("useTeryt", {
        is: false,
        then: yup
          .string()
          .matches(
            new RegExp(/^[a-zA-Z\s\p{L}\/\-]+$/, "u"),
            "Miejscowość zawiera nieprawidłowe znaki. Dozwolone są tylko litery i znaki specjalne (spacja, -, /)"
          )
          .max(50, "Miejscowość może zawierać maksymalnie 50 znaków")
          .required("Miejscowość jest wymagana"),
      }),
    postCode: isZipCodeRequiredWhileVotingNotLoggedUser
      ? yup
          .string()
          .max(6, "Kod pocztowy może zawierać maksymalnie 6 znaków")
          .matches(
            new RegExp(/^[0-9]{2}-[0-9]{3}$/),
            "Kod pocztowy musi składać się z cyfr w formacie xx-xxx"
          )
          .nullable()
          .required("Kod pocztowy jest wymagany")
      : yup
          .string()
          .max(6, "Kod pocztowy może zawierać maksymalnie 6 znaków")
          .matches(
            new RegExp(/^[0-9]{2}-[0-9]{3}$/),
            "Kod pocztowy musi składać się z cyfr w formacie xx-xxx"
          )
          .nullable(),
    street: yup
      .string()
      .matches(
        new RegExp(/^[a-zA-Z0-9\s\p{L}\/\-]+$/, "u"),
        "Ulica zawiera nieprawidłowe znaki. Dozwolone są tylko litery, cyfry i znaki specjalne (spacja, -, /)"
      )
      .max(100, "Ulica może zawierać maksymalnie 100 znaków"),
    streetFeature: yup.number().nullable(),
    number: yup
      .string()
      .matches(
        new RegExp(/^[a-zA-Z0-9\p{L}\/]+$/, "u"),
        "Numer budynku zawiera nieprawidłowe znaki. Dozwolone są tylko litery, cyfry i znaki specjalne (/)"
      )
      .max(15, "Numer budynku może zawierać maksymalnie 15 znaków")
      .required("Numer budynku jest wymagany"),
    flat: yup
      .string()
      .matches(
        new RegExp(/^[a-zA-Z0-9\p{L}\/]+$/, "u"),
        "Numer lokalu zawiera nieprawidłowe znaki. Dozwolone są tylko litery, cyfry i znaki specjalne (/)"
      )
      .max(15, "Numer lokalu może zawierać maksymalnie 15 znaków")
      .nullable(),
    email: yup
      .string()
      .when(
        ["uniqueEmailWhileVotingNotLoggedUser"],
        (uniqueEmailWhileVotingNotLoggedUser) =>
          uniqueEmailWhileVotingNotLoggedUser
            ? yup
                .string()
                .max(100, "E-mail może zawierać maksymalnie 100 znaków")
                .email(
                  "Wartość w polu e-mail nie jest prawidłowym formatem adresu e-mail"
                )
                .required("Adres e-mail jest wymagany")
            : yup
                .string()
                .max(100, "E-mail może zawierać maksymalnie 100 znaków")
                .email(
                  "Wartość w polu e-mail nie jest prawidłowym formatem adresu e-mail"
                )
      ),
    clauseId: yup.number(),
    clauseRequired: yup
      .boolean()
      .when("clauseId", (clauseId) =>
        clauseId > 0
          ? yup.boolean().equals([true], "Wymagana akceptacja oświadczenia")
          : yup.boolean()
      ),
    uniqueEmailWhileVotingNotLoggedUser: yup.boolean(),
    onlyForLegalAge: yup.boolean(),
    terytCity: yup
      .object({
        id: yup.string(),
        label: yup.string(),
        value: yup.number(),
      })
      .nullable()
      .when("useTeryt", {
        is: true,
        then: yup
          .object({
            id: yup.string(),
            label: yup.string(),
            value: yup.number(),
          })
          .required("Miejscowość jest wymagana"),
      }),
  });
};
